import type { Board, Priority, StatusCategory, WorkItemCard, WorkItemType } from './types';

/**
 * A card, plus the two things a cross-project list knows that a board does not: which project
 * it came from, and its status spelt out rather than as an id (status ids differ per project,
 * so "everything blocked for this customer" can only be asked by category).
 */
export interface FilterableItem extends WorkItemCard {
  project?: { id: string; name: string; code: string };
  status?: { id: string; label: string; color: string; category: StatusCategory } | null;
}

/**
 * What a person has narrowed the board down to.
 *
 * One filter for all three views. A filter that meant something different in the table than
 * on the kanban would be worse than none: you would stop trusting the counts.
 */
export interface BoardFilter {
  /** Matches key, title, description and blocked reason. */
  text: string;
  /** Person id, or the two special values. Empty means anybody. */
  assignee: string | 'any' | 'me' | 'none';
  priorities: Priority[];
  types: WorkItemType[];
  /** Service category id, `none` for uncategorised, `any` for either. */
  category: string | 'any' | 'none';
  /** Section (task group) id, `none` for unfiled. */
  group: string | 'any' | 'none';
  /** Project id. Only offered where a list spans more than one. */
  project: string | 'any';
  /** Status by category rather than by id, which is the only way to ask across projects. */
  statusCategory: StatusCategory | 'any';
  /** Quick switches, each narrowing further. */
  overdue: boolean;
  blocked: boolean;
  atRisk: boolean;
  hasMerge: boolean;
  unestimated: boolean;
}

export const EMPTY_FILTER: BoardFilter = {
  text: '',
  assignee: 'any',
  priorities: [],
  types: [],
  category: 'any',
  group: 'any',
  project: 'any',
  statusCategory: 'any',
  overdue: false,
  blocked: false,
  atRisk: false,
  hasMerge: false,
  unestimated: false,
};

/** Whether anything is narrowing the board, which is what the UI has to announce. */
export function isFiltering(filter: BoardFilter): boolean {
  return (
    filter.text.trim() !== '' ||
    filter.assignee !== 'any' ||
    filter.priorities.length > 0 ||
    filter.types.length > 0 ||
    filter.category !== 'any' ||
    filter.group !== 'any' ||
    filter.project !== 'any' ||
    filter.statusCategory !== 'any' ||
    filter.overdue ||
    filter.blocked ||
    filter.atRisk ||
    filter.hasMerge ||
    filter.unestimated
  );
}

/** How many separate conditions are on, for the badge on the Filter button. */
export function activeCount(filter: BoardFilter): number {
  return [
    filter.text.trim() !== '',
    filter.assignee !== 'any',
    filter.priorities.length > 0,
    filter.types.length > 0,
    filter.category !== 'any',
    filter.group !== 'any',
    filter.project !== 'any',
    filter.statusCategory !== 'any',
    filter.overdue,
    filter.blocked,
    filter.atRisk,
    filter.hasMerge,
    filter.unestimated,
  ].filter(Boolean).length;
}

/**
 * Every condition must hold — they narrow, they do not widen. Within one condition, a list
 * means any of those values: "critical or high" is one thought, not two.
 */
export function matches(item: FilterableItem, filter: BoardFilter, meId: string | null): boolean {
  const text = filter.text.trim().toLowerCase();
  if (text) {
    const haystack = [item.key, item.title, item.description, item.blockedReason]
      .join('\n')
      .toLowerCase();
    if (!haystack.includes(text)) return false;
  }

  if (filter.assignee === 'none' && item.assignees.length > 0) return false;
  if (filter.assignee === 'me') {
    if (!meId || !item.assignees.some((person) => person.id === meId)) return false;
  }
  if (filter.assignee !== 'any' && filter.assignee !== 'me' && filter.assignee !== 'none') {
    if (!item.assignees.some((person) => person.id === filter.assignee)) return false;
  }

  if (filter.priorities.length > 0 && !filter.priorities.includes(item.priority)) return false;
  if (filter.types.length > 0 && !filter.types.includes(item.type)) return false;

  if (filter.category === 'none' && item.category) return false;
  if (filter.category !== 'any' && filter.category !== 'none' && item.category?.id !== filter.category) return false;

  if (filter.group === 'none' && item.groupId) return false;
  if (filter.group !== 'any' && filter.group !== 'none' && item.groupId !== filter.group) return false;

  /**
   * A condition the item carries no data for is skipped rather than treated as a miss. These
   * two are only offered on a list that spans projects, so a board can never set them — and
   * dropping every card because a board card has no `project` field would be absurd.
   */
  if (filter.project !== 'any' && item.project && item.project.id !== filter.project) return false;
  if (filter.statusCategory !== 'any' && item.status && item.status.category !== filter.statusCategory) return false;

  if (filter.overdue && !item.overdue) return false;
  if (filter.blocked && !item.blockedSince) return false;
  // Breached counts as at risk: somebody asking "what is in trouble" means both.
  if (filter.atRisk && item.sla?.standing !== 'at_risk' && item.sla?.standing !== 'breached') return false;
  if (filter.hasMerge && !item.merge) return false;
  if (filter.unestimated && item.estimateMinutes !== null && item.points !== null) return false;

  return true;
}

/**
 * The board with the same shape, holding only what matched — so every view keeps reading the
 * one object and none of them needs to know a filter exists.
 */
export function applyFilter(board: Board, filter: BoardFilter, meId: string | null): Board {
  if (!isFiltering(filter)) return board;

  const keep = (items: WorkItemCard[]) => items.filter((item) => matches(item, filter, meId));


  return {
    ...board,
    columns: board.columns.map((column) => ({ ...column, items: keep(column.items) })),
    groups: board.groups.map((group) => ({ ...group, items: keep(group.items) })),
    ungrouped: keep(board.ungrouped),
    unassigned: keep(board.unassigned),
  };
}

/** Cards on a board, counted once. Columns and sections are two views of the same set. */
export function countItems(board: Board): number {
  return board.columns.reduce((total, column) => total + column.items.length, 0) + board.unassigned.length;
}

/** First of each, by whatever identifies it. Facet lists come from cards, which repeat. */
export function dedupe<T>(items: T[], keyOf: (item: T) => string): T[] {
  const seen = new Map<string, T>();
  for (const item of items) if (!seen.has(keyOf(item))) seen.set(keyOf(item), item);
  return [...seen.values()];
}
